import { makeStyles } from '@material-ui/core'
import IconButton from '@material-ui/core/IconButton'
import CloseIcon from '@material-ui/icons/Close'
import clsx from 'clsx'
import { findNumbers } from 'libphonenumber-js'
import PropTypes from 'prop-types'
import * as R from 'ramda'
import { useEffect, useState } from 'react'
import { useIntl } from 'react-intl'
import PhoneInput from 'react-phone-input-2'
import 'react-phone-input-2/lib/material.css'

import LabelError from 'app/componentV4/Labels/LabelError'

const useStyles = makeStyles((theme) => ({
  root: {
    position: 'relative',
    '& .react-tel-input .special-label': {
      display: 'none !important',
    },
    '& .react-tel-input .form-control': {
      backgroundColor: '#FFFFFF',
      borderRadius: '5px',
      height: '48px',
      color: theme.colors.primary,
      width: '100%',
      boxShadow: 'none',
      paddingRight: '40px', // Make space for clear button
      '&:hover': {
        backgroundColor: theme.colors['primary-lighter'],
      },
      '&:focus': {
        boxShadow: 'none',
      },
    },
    '& .react-tel-input .country-list': {
      zIndex: 1050,
      position: 'absolute',
    },
    '& .react-tel-input .country-list .search': {
      padding: '10px',
    },
    '& .react-tel-input .country-list .search-box': {
      margin: 0,
      width: '100%',
    },
  },
  valid: {
    '& .react-tel-input .form-control': {
      border: `1px solid ${theme.colors['grey-100']}`,
      '&:hover': {
        borderColor: theme.colors['grey-100'] + '!important',
      },
      '&:focus': {
        borderColor: theme.colors['grey-100'] + '!important',
      },
      '&:disabled': {
        backgroundColor: theme.colors['neutrals-100'] + '!important',
      },
    },
  },
  error: {
    '& .react-tel-input .form-control': {
      border: `1px solid ${theme.colors['red-500']}`,
      '&:hover': {
        borderColor: theme.colors['red-500'] + '!important',
      },
      '&:focus': {
        borderColor: theme.colors['red-500'] + '!important',
      },
    },
  },
  inputWrapper: {
    position: 'relative',
    width: '100%',
  },
  clearButton: {
    position: 'absolute',
    right: '8px',
    top: '12px',
    zIndex: 1,
    padding: '4px',
    '& svg': {
      fontSize: '18px',
      color: theme.colors['grey-400'],
    },
    '&:hover svg': {
      color: theme.colors['grey-600'],
    },
  },
}))

export const validatePhoneNumber = (phoneNumber, countryCode) => {
  if (R.isEmpty(phoneNumber)) return true
  const isValid = findNumbers(phoneNumber, countryCode, { v2: true }).length > 0
  return isValid
}

const PhoneNumberInput = (props) => {
  const {
    id,
    onChange,
    value,
    isShowDropdownTop = false,
    isDropDownPositionFixed = false,
    ...rest
  } = props
  const classes = useStyles()
  const { formatMessage } = useIntl()

  const [phoneValue, setPhoneValue] = useState(value)
  const [localDialCode, setLocalDialCode] = useState(null)
  const [errorMessage, setErrorMessage] = useState('')
  const [phoneData, setPhoneData] = useState(null)

  const handleChange = (value, data, e, formattedValue) => {
    setPhoneValue(value)
    setPhoneData(data)
    if (value !== data.dialCode) {
      setLocalDialCode('+' + data.dialCode)
    } else {
      setLocalDialCode(null)
    }
    onChange(data, formattedValue)

    if (formattedValue === '+' + data.dialCode) {
      setErrorMessage('')
      return
    }

    const isValid = validatePhoneNumber(formattedValue, data.countryCode)

    if (isValid) {
      setErrorMessage('')
    } else {
      setErrorMessage(
        formatMessage({
          id: 'memberForm.phoneNumberInvalid',
        }),
      )
    }
  }

  const handleClear = (event) => {
    event.stopPropagation()
    setPhoneValue(localDialCode)
    setLocalDialCode(null)
    setErrorMessage('')
    onChange(phoneData, localDialCode)
  }

  useEffect(() => {
    setPhoneValue(value)
  }, [value])

  return (
    <div
      className={clsx(
        classes.root,
        !R.isEmpty(errorMessage) ? classes.error : classes.valid,
      )}
    >
      <div className={classes.inputWrapper}>
        <PhoneInput
          {...rest}
          value={phoneValue}
          enableSearchField
          disableSearchIcon
          disableAreaCodes={true}
          autoFormat={false}
          enableSearch={true}
          countryCodeEditable={false}
          disableCountryGuess={true}
          onChange={handleChange}
          dropdownStyle={{
            zIndex: 1050,
            position: 'absolute',
            ...(isShowDropdownTop ? { bottom: '100%' } : {}),
          }}
          inputExtraProps={{
            InputProps: {
              endAdornment: null,
            },
            autoComplete: 'off',
          }}
          enableTerritories={true}
        />
        {localDialCode && (
          <IconButton
            className={classes.clearButton}
            onClick={handleClear}
            size="small"
            aria-label="clear phone number"
          >
            <CloseIcon />
          </IconButton>
        )}
      </div>
      {!R.isEmpty(errorMessage) && <LabelError id={id} text={errorMessage} />}
    </div>
  )
}

PhoneNumberInput.propTypes = {
  id: PropTypes.string,
  value: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
}

export default PhoneNumberInput
